import {createApi} from "@reduxjs/toolkit/query/react"
import {apiConfig} from "../../utils/api-config"
import {PlaylistsResponse, SinglePlaylistResponse, WeekDaysResponse} from "../../types/Playlist"

export const playlistApi = createApi({
  reducerPath: "playlistApi",
  baseQuery: apiConfig,
  tagTypes: ["Playlists", "Playlist"],
  endpoints: (builder) => ({
    getAllPlaylists: builder.query<PlaylistsResponse, void>({
      query: () => "/playlists",
      providesTags: ["Playlists"]
    }),
    getPlaylistById: builder.query<SinglePlaylistResponse, number | undefined>({
      query: (id) => `/playlists/${id}`,
      providesTags: ["Playlist"]
    }),
    createPlaylistContent: builder.mutation<
      void,
      {
        playlist_id: number
        content_id: number
        content_loop_count?: number
        content_duration?: number
        start_date?: string
        end_date?: string
        repeat_days?: string[]
        start_time?: string
        end_time?: string
      }
    >({
      query: (data) => ({
        url: "playlist-contents",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Playlist"]
    }),
    deletePlaylistContent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/playlist-contents/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Playlist"]
    }),
    updatePlaylistContent: builder.mutation<
      void,
      {
        playlist_id: number
        content_id: number
        content_loop_count?: number
        content_duration?: number
        start_date?: string
        end_date?: string
        repeat_days?: string[]
        start_time?: string
        end_time?: string
        id: number
      }
    >({
      query: (data) => ({
        url: `playlist-contents/${data.id}`,
        method: "PATCH",
        body: {
          playlist_id: data.playlist_id,
          content_id: data.content_id,
          content_loop_count: data.content_loop_count,
          content_duration: data.content_duration,
          start_date: data.start_date,
          end_date: data.end_date,
          repeat_days: data.repeat_days,
          start_time: data.start_time,
          end_time: data.end_time
        }
      }),
      invalidatesTags: ["Playlist"]
    }),
    updatePlaylistContentOrder: builder.mutation<
      void,
      {
        content_id: number
        order: number
      }
    >({
      query: (data) => ({
        url: `playlist-contents/${data.content_id}/content-order?order=${data.order}`,
        method: "PATCH"
      }),
      invalidatesTags: ["Playlist"]
    }),
    createPlaylist: builder.mutation<
      () => void,
      {
        name: string
      }
    >({
      query: (data) => ({
        url: "/playlists",
        method: "POST",
        body: data
      }),
      invalidatesTags: ["Playlists"]
    }),
    updatePlaylist: builder.mutation<
      void,
      {
        name: string
        id: number
      }
    >({
      query: (data) => ({
        url: `/playlists/${data.id}`,
        method: "PATCH",
        body: {
          name: data.name
        }
      }),
      invalidatesTags: ["Playlists", "Playlist"]
    }),
    deletePlaylist: builder.mutation<void, number>({
      query: (id) => ({
        url: `/playlists/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Playlists"]
    }),
    getWeekdays: builder.query<WeekDaysResponse, void>({
      query: () => "/weekdays"
    })
  })
})

export const {
  useGetAllPlaylistsQuery,
  useLazyGetAllPlaylistsQuery,
  useCreatePlaylistMutation,
  useDeletePlaylistMutation,
  useUpdatePlaylistMutation,
  useLazyGetPlaylistByIdQuery,
  useCreatePlaylistContentMutation,
  useUpdatePlaylistContentMutation,
  useUpdatePlaylistContentOrderMutation,
  useGetWeekdaysQuery,
  useDeletePlaylistContentMutation
} = playlistApi
